import React from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';

const PolicyWrapper = styled.div`
  padding: 48px 0;
  overflow-y: auto;
  height: 100vh;

  h1 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }

  h2 {
    font-size: 1.25rem;
    margin-top: 2rem;
    margin-bottom: 0.75rem;
  }

  h3 {
    font-size: 1rem;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
  }

  p {
    margin-bottom: 1rem;
    color: #333;
    line-height: 1.7;
  }

  ul {
    margin-bottom: 1rem;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.7;
      color: #333;
    }
  }
`;

const PrivacyPolicyPage = () => (
  <PolicyWrapper>
    <Container>
      <h1>Rambla Privacy Policy</h1>

      <p>
        This Privacy Policy describes how HAUSMART Inc. (&ldquo;HAUSMART,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; or &ldquo;our&rdquo;) collects, uses, and shares information through the
        HAUSMART mobile application, the website located at hausmart.com, and related services, as
        well as through our Rambla product, including the Rambla websites located at rambla.io and
        ramblas.io, the Rambla web application, Rambla mobile application, and related tools and
        integrations (collectively, the &ldquo;Service&rdquo;).
      </p>

      <p>
        By accessing or using the Rambla Service, you acknowledge that you have read and understand
        this Privacy Policy.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>a. Account Information</h3>
      <p>
        When you create or are invited to a Rambla account, we may collect information such as your
        name, email address, business affiliation, account credentials, and related profile
        information.
      </p>

      <h3>b. Brand and Store Information (Shopify Integration)</h3>
      <p>
        If you install or use the Rambla Shopify App, Rambla may access certain Shopify store data
        strictly as necessary to operate the Rambla Service. This may include:
      </p>
      <ul>
        <li>Product and catalog information</li>
        <li>Collections and inventory metadata</li>
        <li>Store configuration data</li>
        <li>Order-related data (if enabled)</li>
      </ul>

      <p>
        Rambla accesses only the data authorized through Shopify&apos;s permission framework and
        only for purposes of providing Rambla functionality.
      </p>

      <h3>c. Content Data</h3>
      <p>
        We collect and process content uploaded to Rambla, including video, audio, images, text, and
        metadata associated with content creation and publication.
      </p>

      <h3>d. Usage and Technical Data</h3>
      <p>
        We may collect device information, log data, IP address, browser type, and usage analytics
        to operate, maintain, and improve the Rambla Service.
      </p>

      <h2>2. Artificial Intelligence Processing</h2>

      <p>
        Rambla may use artificial intelligence and machine learning technologies (&ldquo;AI
        Services&rdquo;) to process video, audio, imagery, and text in order to automate and enhance
        content creation workflows.
      </p>

      <p>AI Services may perform tasks such as:</p>

      <ul>
        <li>Video and audio transcription</li>
        <li>Content summarization or transformation</li>
        <li>Metadata extraction</li>
        <li>Automated content formatting</li>
        <li>Creative variation generation</li>
      </ul>

      <p>
        <strong>
          No personally identifiable information is shared with AI service providers for the purpose
          of operating AI Services.
        </strong>{' '}
        Where third-party AI infrastructure is used, content is processed solely to provide Rambla
        functionality and is not used to train external public AI models.
      </p>

      <p>
        If you use Rambla services or install the Rambla Shopify App, we may collect and process
        certain store and content-related information from third-party platforms such as Shopify,
        including product catalog data, collections, inventory-related metadata, and other
        information necessary to operate Rambla features. This information is accessed only with
        your authorization and solely for providing Rambla functionality.
      </p>

      <h2>3. How We Use Information</h2>

      <p>We use collected information to:</p>

      <ul>
        <li>Provide and operate the Rambla Service</li>
        <li>Enable Shopify integration and content synchronization</li>
        <li>Automate content workflows using AI Services</li>
        <li>Improve platform performance and security</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>4. Data Sharing</h2>

      <p>
        Rambla does not sell personal information. We may share data only in the following
        circumstances:
      </p>

      <ul>
        <li>With service providers that support Rambla operations</li>
        <li>With Shopify as required for app functionality</li>
        <li>When required by law or legal process</li>
        <li>In connection with a business transfer (e.g., merger or acquisition)</li>
      </ul>

      <p>
        The Service may use automated technologies, including artificial intelligence and machine
        learning, to process video, audio, images, and text in order to support and automate content
        creation workflows within Rambla. These technologies may assist with tasks such as
        transcription, formatting, metadata generation, and content enhancement.
      </p>

      <p>
        No personally identifiable information is shared with artificial intelligence service
        providers for these purposes, and content processed through such tools is used solely to
        provide and improve the Service.
      </p>

      <h2>5. Data Retention</h2>

      <p>
        We retain data only as long as necessary to provide the Rambla Service, comply with legal
        obligations, resolve disputes, and enforce agreements.
      </p>

      <h2>6. Data Security</h2>

      <p>
        Rambla implements reasonable administrative, technical, and organizational safeguards
        designed to protect data against unauthorized access, alteration, disclosure, or
        destruction.
      </p>

      <h2>7. Your Rights</h2>

      <p>
        Depending on your jurisdiction, you may have rights to access, correct, delete, or restrict
        processing of your personal data. Requests may be submitted through the contact information
        provided in the Rambla Service.
      </p>

      <h2>8. International Transfers</h2>

      <p>
        Rambla may process data in jurisdictions outside your country of residence. Where required,
        appropriate safeguards are implemented.
      </p>

      <h2>9. Updates to This Privacy Policy</h2>

      <p>
        We may update this Privacy Policy from time to time. Changes become effective when posted.
      </p>
    </Container>
  </PolicyWrapper>
);

export default PrivacyPolicyPage;
